import { Injectable, inject } from '@angular/core';

import { Fixture, FixtureStage, Game, OptionMarket } from '@cds/betting-offer';
import { BetBuilderOptionGroupInfo } from '@cds/betting-offer/domain-specific/bet-builder';
import { FixtureOffer } from '@cds/generic-interface/response-items/unpacked';
import { ScoreboardSlim } from '@cds/scoreboards/v1';
import { EventModel, Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { SportsConfig } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { remove, sortBy } from 'lodash-es';

import { sortOptions } from '../helpers/option-sorting';
import { CommonFixtureFactory } from './common-fixture.factory';
import { FixtureMarket, FixtureMarketFactory } from './fixture-market.factory';
import { ScoreboardFactory } from './scoreboard-factory.service';

type Market = Game | OptionMarket;

@Injectable({ providedIn: 'root' })
export class FixtureFactory {
    private fixtureFactory = inject(CommonFixtureFactory);
    private marketFactory = inject(FixtureMarketFactory);
    private scoreboardFactory = inject(ScoreboardFactory);
    private sportConfig = inject(SportsConfig);

    create(fixture: Fixture, sourceId?: string, precreatedOptionGroupInfo?: BetBuilderOptionGroupInfo[], fixtureOffers?: FixtureOffer[]): EventModel {
        const event = this.fixtureFactory.create(fixture, sourceId, precreatedOptionGroupInfo);
        const optionGroups = this.getOrderedMarkets(fixture, fixture.isVirtual, fixture.sport.id).map((optionGroup) =>
            this.marketFactory.create(optionGroup),
        );
        if (fixtureOffers?.length) {
            fixtureOffers.forEach((offer) => {
                optionGroups.push(
                    ...this.getOrderedMarkets(offer, fixture.isVirtual, fixture.sport.id).map((optionGroup) =>
                        this.marketFactory.create(optionGroup),
                    ),
                );
            });
        }

        event.optionGroups = optionGroups;
        event.gamesCount = optionGroups.length;
        event.fixtureScoreboard = fixture.scoreboard as ScoreboardSlim;
        event.imgEventId = fixture.addons.imgEventId;

        return event;
    }

    update(model: EventModel, openForBetting: boolean, stage: FixtureStage): void {
        this.fixtureFactory.update(model, openForBetting, stage);
    }

    deleteOptionGroup(event: EventModel, optionGroupId: string): void {
        remove(event.optionGroups, (g) => g.id === optionGroupId);
        // eslint-disable-next-line no-param-reassign
        event.gamesCount = event.optionGroups.length;
    }

    updateOptionGroup(event: EventModel, fixtureMarket: FixtureMarket): void {
        const updated = this.marketFactory.create(fixtureMarket);
        const index = event.optionGroups.findIndex((optionGroup) => optionGroup.id === updated.id);

        if (index === -1) {
            event.optionGroups.push(updated);
        } else {
            // eslint-disable-next-line no-param-reassign
            event.optionGroups[index] = updated;
            // eslint-disable-next-line no-param-reassign
            event.gamesCount = event.optionGroups.length;
        }

        updated.options = sortOptions(updated.options, updated.resultSorting);
    }

    updateScoreboard(event: EventModel, serverScoreboard: ScoreboardSlim): void {
        // eslint-disable-next-line no-param-reassign
        event.scoreboard = this.scoreboardFactory.createScoreboard(serverScoreboard, event);
    }

    private getOrderedMarkets(fixture: { optionMarkets: OptionMarket[]; games: Game[] }, isVirtual: boolean, sportId: number): Market[] {
        const markets: Market[] = [];

        if (fixture.games) {
            markets.push(...fixture.games);
        }

        if (fixture.optionMarkets) {
            if (isVirtual) {
                const order = this.sportConfig.virtuals.marketTypeOrder[sportId] || [];

                markets.push(...sortBy(fixture.optionMarkets, (market, index) => this.getOrder(order, market, index)));
            } else {
                markets.push(...fixture.optionMarkets);
            }
        }

        return markets.filter(isDefined);
    }

    private getOrder(order: string[], market: OptionMarket, index: number): number {
        const marketType = market.parameters.find((param) => param.key === Tv2MarketParameters.MarketType);

        if (marketType) {
            const orderIndex = order.indexOf(marketType.value);

            if (orderIndex !== -1) {
                return orderIndex;
            }
        }

        return order.length + index;
    }
}
